// src/pages/MigrationPage.js
import React, { useState } from 'react';
import { collection, getDocs, doc, getDoc, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';

function MigrationPage() {
  const [migrationLog, setMigrationLog] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [dryRun, setDryRun] = useState(true);

  const log = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    const logEntry = `[${timestamp}] ${type.toUpperCase()}: ${message}`;
    console.log(logEntry);
    setMigrationLog(prev => [...prev, { message: logEntry, type }]);
  };

  const migratePodcastEpisodes = async () => {
    setIsRunning(true);
    setMigrationLog([]);
    
    log('🚀 Starting Episodes Migration...');
    log(`Mode: ${dryRun ? 'DRY RUN (Safe)' : 'LIVE MIGRATION'}`);

    try {
      // Step 1: Check existing episodes collection
      log('🔍 Checking existing episodes collection...');
      const existingEpisodes = await getDocs(collection(db, 'episodes'));
      log(`📊 Found ${existingEpisodes.size} existing episodes in episodes collection`);

      // Step 2: Extract episodes from podcast documents
      log('📥 Extracting episodes from podcast documents...');
      const podcastsSnapshot = await getDocs(collection(db, 'podcasts'));
      
      let totalEpisodesFound = 0;
      const episodesToMigrate = [];

      for (const podcastDoc of podcastsSnapshot.docs) {
        const podcastData = podcastDoc.data();
        const podcastId = podcastDoc.id;
        
        log(`🎙️  Processing: ${podcastData.title || 'Untitled Podcast'}`);
        
        if (podcastData.episodes && typeof podcastData.episodes === 'object') {
          const episodes = Object.values(podcastData.episodes);
          log(`   Found ${episodes.length} episodes`);
          
          episodes.forEach(episode => {
            if (episode.id) {
              const episodeDoc = {
                // Core data
                id: episode.id,
                title: episode.title || 'Untitled Episode',
                description: episode.description || '',
                audioURL: episode.audioURL || '',
                duration: episode.duration || 0,
                
                // Timestamps
                publishedAt: episode.publishedAt,
                createdAt: episode.publishedAt || new Date(),
                
                // Podcast relationship
                podcastId: podcastId,
                podcastTitle: podcastData.title || 'Unknown Podcast',
                
                // Stats
                listens: episode.listens || 0,
                likes: episode.likes || 0,
                dislikes: episode.dislikes || 0,
                likedBy: episode.likedBy || [],
                dislikedBy: episode.dislikedBy || [],
                
                // Metadata
                episodeNumber: episode.episodeNumber || null,
                seasonNumber: episode.seasonNumber || null,
                isActive: true,
                
                // Migration info
                migratedAt: new Date(),
                migratedFrom: `podcasts/${podcastId}`
              };
              
              episodesToMigrate.push({
                id: episode.id,
                data: episodeDoc,
                sourcePodcast: podcastId
              });
              
              totalEpisodesFound++;
            }
          });
          
          log(`   ✅ Processed ${episodes.length} episodes from ${podcastData.title}`);
        } else {
          log(`   📭 No episodes found in this podcast`);
        }
      }

      log(`🎯 EXTRACTION COMPLETE: ${totalEpisodesFound} episodes found`);

      // Step 3: Show sample episodes
      log(`📋 SAMPLE EPISODES TO MIGRATE:`);
      episodesToMigrate.slice(0, 5).forEach((episode, index) => {
        log(`   ${index + 1}. "${episode.data.title}" -> ${episode.data.podcastTitle}`);
      });

      if (episodesToMigrate.length > 5) {
        log(`   ... and ${episodesToMigrate.length - 5} more episodes`);
      }

      // Step 4: Migration or dry run
      if (dryRun) {
        log('🔍 DRY RUN MODE - No changes made');
        log('✅ MIGRATION PLAN LOOKS GOOD!');
        log('Toggle "Dry Run" off and click "Run Migration" to proceed');
      } else {
        log('🚀 LIVE MIGRATION STARTING...');
        
        let migrated = 0;
        let skipped = 0;
        
        // Migrate in batches
        const batchSize = 50;
        
        for (let i = 0; i < episodesToMigrate.length; i += batchSize) {
          const batch = episodesToMigrate.slice(i, i + batchSize);
          log(`📦 Migrating batch ${Math.floor(i/batchSize) + 1}/${Math.ceil(episodesToMigrate.length/batchSize)}...`);
          
          const writeBatchRef = writeBatch(db);
          
          for (const episode of batch) {
            try {
              // Check if episode already exists
              const episodeRef = doc(db, 'episodes', episode.id);
              const existingEpisode = await getDoc(episodeRef);
              
              if (existingEpisode.exists()) {
                log(`   ⏭️  Episode "${episode.data.title}" already exists, skipping`);
                skipped++;
                continue;
              }
              
              // Add to batch
              writeBatchRef.set(episodeRef, episode.data);
              migrated++;
              
            } catch (error) {
              log(`   ❌ Error with episode ${episode.id}: ${error.message}`, 'error');
              skipped++;
            }
          }
          
          // Commit batch
          try {
            await writeBatchRef.commit();
            log(`   ✅ Batch committed successfully`);
          } catch (error) {
            log(`   ❌ Batch commit failed: ${error.message}`, 'error');
          }
        }
        
        log(`🎉 MIGRATION COMPLETED!`);
        log(`   Episodes migrated: ${migrated}`);
        log(`   Episodes skipped: ${skipped}`);
        log(`   Total processed: ${migrated + skipped}`);
        
        // Verify migration
        log('🔍 Verifying migration...');
        const newEpisodesSnapshot = await getDocs(collection(db, 'episodes'));
        log(`✅ Episodes collection now contains: ${newEpisodesSnapshot.size} episodes`);
      }

    } catch (error) {
      log(`💥 Migration failed: ${error.message}`, 'error');
      console.error('Migration error:', error);
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div style={{ 
      padding: '20px', 
      maxWidth: '1000px', 
      margin: '0 auto',
      fontFamily: 'monospace'
    }}>
      <h1>📦 Episode Migration Tool</h1>
      <p>This tool extracts episodes from podcast documents and creates a separate episodes collection.</p>
      
      {/* Controls */}
      <div style={{ 
        background: '#f5f5f5', 
        padding: '20px', 
        borderRadius: '8px',
        marginBottom: '20px'
      }}>
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input 
              type="checkbox" 
              checked={dryRun}
              onChange={(e) => setDryRun(e.target.checked)}
              style={{ transform: 'scale(1.2)' }}
            />
            <span style={{ fontWeight: 'bold' }}>
              Dry Run Mode {dryRun ? '(Safe - No Changes)' : '(LIVE - Will Modify Database)'}
            </span>
          </label>
        </div>
        
        <button 
          onClick={migratePodcastEpisodes}
          disabled={isRunning}
          style={{
            padding: '12px 24px',
            background: dryRun ? '#4a69dd' : '#e74c3c',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: isRunning ? 'not-allowed' : 'pointer',
            fontSize: '16px',
            fontWeight: 'bold'
          }}
        >
          {isRunning ? '⏳ Running...' : (dryRun ? '🔍 Run Dry Run' : '🚀 Run Live Migration')}
        </button>
        
        {!dryRun && (
          <p style={{ 
            color: '#e74c3c', 
            fontWeight: 'bold',
            marginTop: '10px',
            marginBottom: '0'
          }}>
            ⚠️ WARNING: Live migration will modify your database!
          </p>
        )}
      </div>

      {/* Migration Log */}
      <div style={{ 
        background: '#1a1a1a', 
        color: '#00ff00',
        padding: '20px',
        borderRadius: '8px',
        height: '400px',
        overflowY: 'auto',
        fontSize: '14px',
        lineHeight: '1.4'
      }}>
        <h3 style={{ color: '#00ff00', marginTop: '0' }}>Migration Log:</h3>
        {migrationLog.length === 0 ? (
          <p style={{ color: '#888' }}>Click "Run Dry Run" to start...</p>
        ) : (
          migrationLog.map((entry, index) => (
            <div 
              key={index}
              style={{ 
                color: entry.type === 'error' ? '#ff4444' : '#00ff00',
                marginBottom: '2px'
              }}
            >
              {entry.message}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default MigrationPage;